'use client'

/**
 * AI Chat Floating Action Button (Issue #4429, #4435, #4680, #6460)
 *
 * Bottom-right FAB that toggles the AI chat panel.
 * Hidden on auth, error, and AI-driven conversation surfaces via the
 * shared `shouldHideAiChat` helper — see ai-chat-provider.tsx.
 */

import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { usePresentationMode } from '@/contexts/presentation-mode-context'
import { useRouteErrorActive } from '@/hooks/use-route-error-active'
import { useFabReservation, useSidecarWidth } from '@/components/app-frame'
import { useAiChat, shouldHideAiChat } from './ai-chat-provider'
import { AI_FAB_CLEARANCE_PX } from './ai-chat-layout'
import {
  SaiChatDotAnimated,
  type SaiChatDotHandle,
} from './sai-chat-dot-animated'

/**
 * `AI_FAB_CLEARANCE_CLASS` used to live here, because the FAB is the source
 * of the dimension (#7788). It still is — it now lives NEXT to the other half
 * of that dimension, in `ai-chat-layout.ts`, which is the module that exists
 * so the frame and the FAB can share a geometry constant without importing a
 * component (#13731).
 */
export const MEETING_PRESENTER_VIEW_ATTRIBUTE = 'data-meeting-presenter-view'

function useMeetingPresenterViewActive() {
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const read = () => {
      setActive(html.getAttribute(MEETING_PRESENTER_VIEW_ATTRIBUTE) === 'true')
    }

    read()
    const observer = new MutationObserver(read)
    observer.observe(html, {
      attributes: true,
      attributeFilter: [MEETING_PRESENTER_VIEW_ATTRIBUTE],
    })
    return () => observer.disconnect()
  }, [])

  return active
}

export function AiChatFab() {
  // #13407 — `isInPageChatActive` is true while a page hosts the conversation
  // itself (Home). Read from the provider rather than derived from the
  // pathname, because `/today` renders the same Home tree behind the
  // `home_view` flag and a pathname cannot see a flag.
  const { isOpen, toggleChat, isInPageChatActive } = useAiChat()
  const pathname = usePathname()

  // useSearchParams can return null during prerender — the helper handles
  // null safely (#5520, #6460).
  const searchParams = useSearchParams()

  // Issue #7788: Hide the FAB while presentation mode is active so the
  // chat button doesn't overlay a presented view.
  const { isPresentationMode } = usePresentationMode()
  const isMeetingPresenterView = useMeetingPresenterViewActive()

  // #13956 — a segment's `error.tsx` is the same category as the surfaces
  // above: there is no page context left to ask about, because the tree
  // `use-page-context.ts` reads is the one that threw. It cannot be a
  // pathname rule — an errored `/meetings` has the pathname of a healthy one
  // — so it arrives through `isSurfaceSuppressed` below, which is exactly
  // "a posture struck inside a route rather than a route".
  const isRouteErrorActive = useRouteErrorActive()

  // Issue #12946: the FAB is viewport-fixed and the docked chat occupies the
  // rightmost `sidecarWidth` px of that same viewport, so without this the
  // button sits on top of the panel it opened. Shift left by the reserved
  // width, keeping the 1.5rem gutter `right-6` already spends. 0 (closed,
  // mobile, chat-disabled, hidden route) leaves the class untouched. While
  // the chat is open the FAB is `hidden` on every viewport (see the className
  // below), so on mobile the full-screen dialog's own close control is the
  // only close affordance.
  const sidecarWidth = useSidecarWidth()

  // #13332 — the design system's Dot exposes an `onClick` state-machine
  // trigger. Held here rather than inside the Dot because only the button
  // knows it was activated (the canvas is `pointer-events-none`), and it is a
  // no-op whenever the Dot is static.
  const dotRef = useRef<SaiChatDotHandle>(null)

  // Home registers its in-page chat in an effect, and this FAB lives inside
  // the sidecar's `<Suspense>` — a streamed boundary that can hydrate AFTER
  // that effect has already run. Reading `isInPageChatActive` on the first
  // client render therefore returns null where the server rendered a button,
  // which React reports as a hydration mismatch. Match the server first, then
  // let the suppression take effect on the next commit. `AiChatPanel` carries
  // the same latch for the same reason.
  const [hasHydrated, setHasHydrated] = useState(false)
  useEffect(() => {
    setHasHydrated(true)
  }, [])

  const handleClick = () => {
    // Toggle FIRST, unconditionally: opening the panel must not wait on — or
    // be reachable only through — a Rive animation that may never have loaded.
    toggleChat()
    dotRef.current?.trigger('onClick')
  }

  const hidden = shouldHideAiChat(
    pathname,
    searchParams,
    isPresentationMode ||
      isMeetingPresenterView ||
      // The latch is load-bearing for `isInPageChatActive` only. The route
      // error read needs no help: `useSyncExternalStore` returns
      // `getServerSnapshot()` — `false` — for the whole of hydration, so it
      // cannot disagree with the button the server rendered. It shares the
      // latch to stay one expression, and because suppressing one commit
      // later than strictly necessary is the harmless direction.
      (hasHydrated && (isInPageChatActive || isRouteErrorActive)),
  )

  // #13731 — the FAB is `fixed bottom-6 right-6 z-50` with live
  // `pointer-events`, so at maximum scroll it does not merely obscure the last
  // row of a scrollport's content, it swallows its clicks. Publish the band it
  // occupies so any page-level scrollport can reserve it with one class
  // (`.ai-fab-clear-bottom`). 0 when hidden, so a FAB-suppressed route gains
  // no dead space — the mistake the horizontal half had to correct for in
  // presentation mode.
  //
  // CALLED ABOVE THE EARLY RETURN, and that is not a style preference: the
  // return below is conditional on exactly the `hidden` boolean this reads, so
  // a hook placed after it is a conditional hook that tears the component down
  // on the first route transition flipping visibility (#3161). `hidden` is
  // computed here rather than inlined into the `if` for the same reason.
  //
  // This rides the same hydration timeline as the button: `hasHydrated` gates
  // the in-page-chat suppression above, so the reservation appears when the
  // button does rather than before it.
  //
  // This button is CSS-hidden (not unmounted) while the chat is open, and
  // the reservation deliberately stays up through that: the alternative is
  // 80px of padding appearing and disappearing under the user's cursor
  // every time they toggle the chat, mid-scroll. Over-reserving for the
  // duration of an open panel is the cheaper of the two.
  useFabReservation(hidden ? 0 : AI_FAB_CLEARANCE_PX)

  if (hidden) {
    return null
  }

  return (
    <button
      onClick={handleClick}
      className={`fixed bottom-6 right-6 z-50 flex pointer-coarse:min-h-11 items-center gap-2
             rounded-full border border-stroke-regular bg-surface-elevated px-4 py-2
             text-sm font-semibold text-text-primary shadow-md
             transition-all duration-200 ease-out hover:bg-surface-panel
             motion-reduce:transition-none ${isOpen ? 'hidden' : ''}`}
      style={
        sidecarWidth > 0
          ? { right: `calc(${sidecarWidth}px + 1.5rem)` }
          : undefined
      }
      // This button is `hidden` while the chat is open (see the className
      // above), so it can only ever OPEN — 'Close AI Chat' would name an
      // action it cannot perform.
      //
      // The panel carries its own close control, so nothing is unreachable
      // either way; whether the SchoolAI FAB should toggle in place rather
      // than vanish is a design question left to the design system rather than
      // answered here (#13240 review, suggestion 21).
      aria-label="Open AI Chat"
      data-testid="ai-chat-fab"
      type="button"
    >
      <SaiChatDotAnimated ref={dotRef} size={24} />
      <span>Chat</span>
    </button>
  )
}
